import { useCallback, useMemo } from 'react';
import {
  Colors,
  Flex,
  IconHeartFilled,
  IconHeartOutline,
  List,
  RemoteImage,
  Spinner,
  StackScreen,
  Typography,
} from '@banhala/ui-kit';
import { useFavoriteMarket, useHandleBackPressed } from 'shared/src/hooks';
import { AnalyticsScreen } from 'shared/src/context';
import { CartIcon, SearchIcon } from 'shared/src/component';
import {
  formatNumber,
  localizeNumber,
  useNavigator,
} from 'shared/src/utils';
import type { FavoriteMarket } from 'shared/src/api/favoriteMarketService/getFavoriteMarkets/types';
import { FavoriteMarketEmpty } from './components/FavoriteMarketEmpty';
import { useLogClickMarket } from './hooks/useLogClickMarket';
import { useLogViewFavoriteMarket } from './hooks/useLogViewFavoriteMarket';
import { useFavoriteMarkets } from './queries/useFavoriteMarkets';

const SCREEN_NAME = 'FAVORITE_MARKET';
const MARKET_AVATAR_SIZE = 56;
const NEW_GOODS_BADGE_LIMIT = 99;

export const LikeMarketListScreen = () => {
  const { onPressBack, showBackButton } = useHandleBackPressed();

  return (
    <AnalyticsScreen screen={SCREEN_NAME}>
      <StackScreen
        title={'찜한 마켓'}
        hideBackButton={!showBackButton}
        onPressBack={onPressBack}
        rightIcons={[<SearchIcon />, <CartIcon />]}
      >
        <LikeMarketListContent />
      </StackScreen>
    </AnalyticsScreen>
  );
};

const LikeMarketListContent = () => {
  const {
    data,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
  } = useFavoriteMarkets();

  useLogViewFavoriteMarket();

  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const listId = data?.list_id ?? '';
  const renderItem = useCallback(
    ({ item, index }: { item: FavoriteMarket; index: number }) => (
      <MarketRow market={item} index={index} listId={listId} />
    ),
    [listId],
  );

  if (data == null) {
    return (
      <Flex.Center flex={1}>
        <Spinner color={'content_tertiary'} size={24} />
      </Flex.Center>
    );
  }

  const isEmpty = data.markets.length === 0;

  return (
    <List<FavoriteMarket>
      data={data.markets}
      keyExtractor={market => String(market.sno)}
      ListHeaderComponent={
        isEmpty ? (
          <FavoriteMarketEmpty />
        ) : (
          <TotalCount count={data.total_count} />
        )
      }
      ListFooterComponent={
        isFetchingNextPage ? (
          <Flex.Center padding={{ vertical: 16 }}>
            <Spinner color={'content_tertiary'} size={20} />
          </Flex.Center>
        ) : null
      }
      renderItem={renderItem}
      contentContainerStyle={{ paddingBottom: 100, flexGrow: 1 }}
      onEndReached={handleEndReached}
      onEndReachedThreshold={0.5}
      refreshing={isRefetching}
      onRefresh={refetch}
      showsVerticalScrollIndicator={false}
    />
  );
};

const TotalCount = ({ count }: { count: number }) => (
  <Flex padding={{ horizontal: 16, vertical: 12 }}>
    <Typography type={'body2'} color={'content_tertiary'}>
      {`마켓 ${localizeNumber(count)}개`}
    </Typography>
  </Flex>
);

interface MarketRowProps {
  market: FavoriteMarket;
  index: number;
  listId: string;
}

const MarketRow = ({ market, index, listId }: MarketRowProps) => {
  const { navigator } = useNavigator();
  const logClickMarket = useLogClickMarket();

  const logging = useMemo(
    () => ({
      analytics: {
        SCREEN: SCREEN_NAME,
        MARKET_NAME: market.name,
        MARKET_SNO: String(market.sno),
        IS_NEW: market.new_goods_count > 0,
        NEW_GOODS_COUNT: String(market.new_goods_count),
        MARKET_TYPE: market.market_type?.name,
        LIST_ID: listId,
        INDEX: String(index),
      },
    }),
    [
      market.name,
      market.sno,
      market.new_goods_count,
      market.market_type?.name,
      listId,
      index,
    ],
  );

  const { isFavorited, favoritesCount, toggleFavorite } = useFavoriteMarket({
    sno: market.sno,
    initialFavorited: market.favorite != null,
    initialFavoritesCount: market.favorites_count,
    logging,
  });

  const handlePressMarket = useCallback(() => {
    logClickMarket({ logging });
    navigator.moveToMarketHome({ sno: market.sno });
  }, [logClickMarket, logging, navigator, market.sno]);

  return (
    <Flex.Row
      alignItems={'center'}
      padding={{ left: 16, right: 4, top: index === 0 ? 8 : 12 }}
    >
      <Flex.Row flex={1} alignItems={'center'} onPress={handlePressMarket}>
        <Flex.Center
          width={MARKET_AVATAR_SIZE}
          height={MARKET_AVATAR_SIZE}
          borderRadius={MARKET_AVATAR_SIZE / 2}
          borderWidth={1}
          borderColor={'border_transparent'}
          overflow={'hidden'}
        >
          <RemoteImage
            uri={market.image ?? market.badge ?? ''}
            width={MARKET_AVATAR_SIZE}
            height={MARKET_AVATAR_SIZE}
          />
        </Flex.Center>
        <Flex.Row
          flex={1}
          alignItems={'center'}
          gap={4}
          padding={{ horizontal: 12 }}
        >
          <Flex flexShrink={1}>
            <Typography
              type={'subtitle1'}
              color={'content_primary'}
              numberOfLines={1}
              ellipsizeMode={'tail'}
            >
              {market.name}
            </Typography>
          </Flex>
          {market.new_goods_count > 0 && (
            <NewGoodsBadge count={market.new_goods_count} />
          )}
        </Flex.Row>
      </Flex.Row>
      <FavoriteButton
        isFavorited={isFavorited}
        count={favoritesCount}
        onPress={toggleFavorite}
      />
    </Flex.Row>
  );
};

const NewGoodsBadge = ({ count }: { count: number }) => (
  <Flex.Center
    minWidth={22}
    padding={{ horizontal: 4, vertical: 2 }}
    borderRadius={50}
    backgroundColor={'bg_black_button'}
  >
    <Typography type={'subtitle6'} color={'content_always_white'}>
      {`+${Math.min(count, NEW_GOODS_BADGE_LIMIT)}`}
    </Typography>
  </Flex.Center>
);

interface FavoriteButtonProps {
  isFavorited: boolean;
  count: number;
  onPress: () => void;
}

const FavoriteButton = ({
  isFavorited,
  count,
  onPress,
}: FavoriteButtonProps) => {
  const { value, label } = formatNumber(count);
  const HeartIcon = isFavorited ? IconHeartFilled : IconHeartOutline;
  return (
    <Flex.Center width={46} gap={4} onPress={onPress}>
      <HeartIcon width={20} height={20} fill={Colors.content_pink_primary} />
      <Typography type={'body5'} color={'content_secondary'}>
        {label === '' ? localizeNumber(value) : `${value}${label}`}
      </Typography>
    </Flex.Center>
  );
};
